import { IconName } from '@wui/common-library'
import { isBotPServed } from '@wui/im'
import { computed, Ref, ref } from 'vue'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { MENTION_ALL_USER_ID } from '@/shared/services/mention-helper'
import { Contact } from '@/shared/types/api'
import { Room } from '@/shared/types/store'
import { isBotMemberNumber } from '@/shared/utils/contacts'
import { fromPServedUser } from '@/shared/utils/uri'

export type PickAllOption = {
  icon: IconName,
  title: string,
  subtitle: string,
}

export type MentionOption = PickAllOption | Contact

export type OptionsTuple = MentionOption[]

export const isPickAllOption = (option: MentionOption): option is PickAllOption =>
  'icon' in option && 'title' in option

const getContactNickname = (contact: Contact) =>
  fromPServedUser(contact.imLogin || '').toLocaleLowerCase()

const getContactSortKey = (contact: Contact) =>
  `${contact.lastName ?? ''} ${contact.firstName ?? ''} ${contact.name}`.trim().toLocaleLowerCase()

export function useMention (input: Ref, room: Ref<Room>) {
  const { t } = useLocalization()
  const isMentioning = ref(false)
  const filter = ref('')
  const { currentUser } = useCurrentUser()
  const currentUserToRemove = currentUser.value?.internalNumber?.toLocaleLowerCase()
  const currentUserImLogin = currentUser.value?.imLogin?.toLocaleLowerCase()
  const initialContacts = computed(() =>
    Array
      .from(room.value.subscribers.values())
      .filter((contact) => {
        if (contact.type === 'pstnGuest' && !isBotPServed(contact.imLogin) && !isBotMemberNumber(contact.id)) {
          return false
        }

        const internalNumber = contact.internalNumber?.toLocaleLowerCase()
        if (internalNumber && internalNumber === currentUserToRemove) {
          return false
        }

        const imLogin = contact.imLogin?.toLocaleLowerCase()
        if (imLogin && imLogin === currentUserImLogin) {
          return false
        }

        return Boolean(contact.imLogin) || isBotMemberNumber(contact.id)
      }))

  const sortedContacts = computed(() =>
    [...initialContacts.value].sort((a, b) =>
      getContactSortKey(a).localeCompare(getContactSortKey(b), undefined, { sensitivity: 'base' }),
    ),
  )

  const pickAllOption = computed<PickAllOption>(() => ({
    icon: 'userGroupM',
    title: `@${t('common.all')}`,
    subtitle: t('chat.allChatParticipants'),
  }))

  const matchesAllFilter = (filterStr: string) => {
    if (!filterStr) return true
    const normalized = filterStr.toLocaleLowerCase()
    const allLabel = t('common.all').toLocaleLowerCase()
    return allLabel.startsWith(normalized)
      || normalized === 'all'
      || 'all'.startsWith(normalized)
  }

  const availableOptions = computed<OptionsTuple>(() => {
    const normalizedFilter = filter.value.toLocaleLowerCase()
    const contacts = sortedContacts.value.filter((contact) => {
      if (!normalizedFilter) return true
      return contact.name.toLocaleLowerCase().includes(normalizedFilter)
        || getContactNickname(contact).includes(normalizedFilter)
    })

    const options: MentionOption[] = []
    if (matchesAllFilter(normalizedFilter)) {
      options.push(pickAllOption.value)
    }
    options.push(...contacts)
    return options
  })

  const selectedOptionIndex = ref(0)

  const startMention = () => {
    isMentioning.value = true
  }

  const endMention = () => {
    isMentioning.value = false
    selectedOptionIndex.value = 0
  }

  const updateMention = () => {
    const range = getRange()

    if (!range || !range.collapsed) {
      endMention()
      return
    }

    const nodeText = getTextNodeTextContent(range)
    const caretPositionAtNode = range.startOffset

    if (!nodeText) {
      endMention()
      return
    }

    let startMentionTextPosition = null
    let i = caretPositionAtNode - 1

    while (
      !startMentionTextPosition &&
      i > -1
    ) {
      const char = nodeText.charAt(i)
      const nextChar = nodeText.charAt(i - 1)
      if (char === '@' && (!nextChar || nextChar.match(/\W/))) {
        startMentionTextPosition = i + 1
      }
      i--
    }
    let mentionStr = null
    if (startMentionTextPosition != null && startMentionTextPosition > -1) {
      startMention()
      mentionStr = nodeText.slice(startMentionTextPosition, caretPositionAtNode)
    } else {
      endMention()
    }
    filter.value = mentionStr || ''
    if (selectedOptionIndex.value >= availableOptions.value.length) {
      selectedOptionIndex.value = 0
    }
  }

  const getTextNodeTextContent = (range: Range): string | null => {
    let result = null
    const node = range.startContainer
    if (node.nodeType === Node.TEXT_NODE) {
      result = node.textContent
    }
    return result
  }

  const addMention = (option?: MentionOption) => {
    const selection = document.getSelection()
    const range = selection?.getRangeAt(0)
    const mentionTextLength = filter.value.length
    if (range && range.collapsed) {
      range.setStart(
        range.startContainer,
        range.startOffset - mentionTextLength - 1,
      )
      range.deleteContents()

      const target = option ?? availableOptions.value[selectedOptionIndex.value]
      if (!target) return

      const contact = isPickAllOption(target)
        ? { imLogin: MENTION_ALL_USER_ID, name: t('common.all') } as Contact
        : target

      range.insertNode(createMentionElement(contact))
      range.collapse()
      range.insertNode(createWhitespaceElement())
      range.collapse()
      input.value.update()
    }
  }

  const createMentionElement = (contact: Contact) => {
    const contactUri = contact.imLogin
    const contactName = contact.name

    const newNode = document.createElement('b')
    newNode.setAttribute('contenteditable', 'false')
    newNode.setAttribute('data-contact-uri', contactUri)
    newNode.className = 'mention-chip'
    newNode.innerText = `@${contactName}`

    return newNode
  }

  const createWhitespaceElement = () => {
    return document.createTextNode(' ')
  }

  const tryAutoCompleteByNickname = (): boolean => {
    if (!isMentioning.value) return false

    const normalizedFilter = filter.value.toLocaleLowerCase()
    if (!normalizedFilter) return false

    const allLabel = t('common.all').toLocaleLowerCase()
    if (normalizedFilter === 'all' || normalizedFilter === allLabel) {
      addMention(pickAllOption.value)
      endMention()
      return true
    }

    const exactContact = sortedContacts.value.find(
      (contact) => getContactNickname(contact) === normalizedFilter,
    )
    if (!exactContact) return false

    addMention(exactContact)
    endMention()
    return true
  }

  const tryDeleteMentionChip = (): boolean => {
    const range = getRange()
    if (!range?.collapsed) return false

    let mentionEl: HTMLElement | null = null
    const { startContainer, startOffset } = range

    if (startContainer.nodeType === Node.TEXT_NODE && startOffset === 0) {
      const prev = startContainer.previousSibling
      if (prev instanceof HTMLElement && prev.hasAttribute('data-contact-uri')) {
        mentionEl = prev
      }
    } else if (startContainer.nodeType === Node.ELEMENT_NODE && startOffset > 0) {
      const child = startContainer.childNodes[startOffset - 1]
      if (child instanceof HTMLElement && child.hasAttribute('data-contact-uri')) {
        mentionEl = child
      }
    }

    if (!mentionEl) return false

    const selection = document.getSelection()
    if (!selection) return false

    const atNode = document.createTextNode('@')
    mentionEl.parentNode?.insertBefore(atNode, mentionEl)
    mentionEl.remove()

    const nextRange = document.createRange()
    nextRange.setStart(atNode, 1)
    nextRange.collapse(true)
    selection.removeAllRanges()
    selection.addRange(nextRange)

    input.value.update()
    updateMention()
    return true
  }

  const handleKeyDown = (e: KeyboardEvent): boolean => {
    if (e.key === 'Backspace' && tryDeleteMentionChip()) {
      e.preventDefault()
      return false
    }

    if (isMentioning.value && (e.key === ' ' || e.code === 'Space') && tryAutoCompleteByNickname()) {
      e.preventDefault()
      return true
    }

    if (!isMentioning.value || availableOptions.value.length === 0) {
      return false
    }

    let mentionAdded = false
    if (e.key === 'Enter') {
      e.preventDefault()
      addMention()
      mentionAdded = true
      endMention()
    } else if (e.code === 'Escape') {
      e.preventDefault()
      endMention()
    } else if (e.code === 'ArrowUp') {
      e.preventDefault()
      let newIndex = selectedOptionIndex.value - 1
      if (newIndex < 0) {
        newIndex = availableOptions.value.length - 1
      }
      selectedOptionIndex.value = newIndex
    } else if (e.code === 'ArrowDown') {
      e.preventDefault()
      let newIndex = selectedOptionIndex.value + 1
      if (newIndex >= availableOptions.value.length) {
        newIndex = 0
      }
      selectedOptionIndex.value = newIndex
    }

    if (e.code === 'ArrowLeft' || e.code === 'ArrowRight') {
      setTimeout(() => {
        updateMention()
      }, 100)
    }

    return mentionAdded
  }

  const getRange = (): Range | undefined => {
    const isSupported = typeof window.getSelection !== 'undefined'
    if (!isSupported) {
      return undefined
    }
    const selection = window.getSelection()
    if (!selection) {
      throw new Error('no selection')
    }
    if (selection.rangeCount !== 0) {
      return selection.getRangeAt(0)
    }
    return
  }

  const handleOptionClick = (index: number) => {
    selectedOptionIndex.value = index
    addMention()
    endMention()
  }

  return {
    isMentioning,
    startMention,
    initialContacts,
    availableOptions,
    selectedOptionIndex,
    endMention,
    updateMention,
    handleOptionClick,
    handleKeyDown,
  }
}
